import {
  DateField,
  DeleteButton,
  List,
  ShowButton,
  TagField,
  TextField,
  useTable,
} from "@refinedev/antd";
import { IResourceComponentsProps } from "@refinedev/core";
import { Button, Divider, Modal, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { getStatusTagColor } from "constants/tagColors";
import {
  isOrderUploadTerminal,
  ORDER_UPLOAD_STAGE_LABELS,
} from "constants/orderUploadStages";
import { IOrderUploadStatus } from "interfaces";
import React, { useState } from "react";

const STALLED_AFTER_MS = 10 * 60 * 1000;

const isStalled = (record: IOrderUploadStatus): boolean =>
  !isOrderUploadTerminal(record.stage) &&
  Date.now() - new Date(record.updatedAt).getTime() > STALLED_AFTER_MS;

export const OrderUploadStatusList: React.FC<IResourceComponentsProps> = () => {
  const [errorRecord, setErrorRecord] = useState<IOrderUploadStatus | null>(
    null
  );

  const { tableProps, tableQuery: { isLoading } } = useTable<IOrderUploadStatus>({
    sorters: {
      initial: [
        {
          field: "createdAt",
          order: "desc",
        },
      ],
    },
    queryOptions: {
      refetchInterval: (query: any) => {
        const rows: IOrderUploadStatus[] = query?.state?.data?.data ?? [];
        return rows.some((row) => !isOrderUploadTerminal(row.stage))
          ? 5000
          : false;
      },
    },
  });

  const columns: ColumnsType<IOrderUploadStatus> = [
    {
      title: "Uploaded At",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 170,
      render: (value) => <DateField format="DD-MMM-YYYY HH:mm" value={value} />,
      sorter: true,
    },
    {
      title: "File",
      dataIndex: "fileName",
      key: "fileName",
      width: 220,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
    },
    {
      title: "IMO Number",
      dataIndex: "imoNumber",
      key: "imoNumber",
      width: 130,
      render: (value) => <TextField value={value ?? "-"} />,
    },
    {
      title: "Vessel Name",
      dataIndex: "vesselName",
      key: "vesselName",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value ?? "-"} />,
    },
    {
      title: "Uploaded By",
      dataIndex: "userEmail",
      key: "userEmail",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value ?? "-"} />,
    },
    {
      title: "Status",
      dataIndex: "stage",
      key: "stage",
      width: 190,
      render: (value, record) => (
        <Space>
          <TagField
            color={getStatusTagColor(value)}
            value={ORDER_UPLOAD_STAGE_LABELS[value] ?? value}
          />
          {isStalled(record) && <Tag color="orange">stalled</Tag>}
        </Space>
      ),
    },
    {
      title: "Errors",
      key: "errors",
      width: 110,
      render: (_, record) =>
        record.errors?.length ? (
          <Button size="small" danger onClick={() => setErrorRecord(record)}>
            {record.errors.length} error{record.errors.length > 1 ? "s" : ""}
          </Button>
        ) : (
          <TextField value="-" />
        ),
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          {record.orderId ? (
            <ShowButton
              hideText
              size="small"
              resource="order"
              recordItemId={record.orderId}
            />
          ) : null}
          <DeleteButton
            hideText
            size="small"
            recordItemId={record.id}
            accessControl={{ hideIfUnauthorized: true }}
          />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      title="Upload Status"
      headerProps={{
        subTitle: "order file processing status",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1100 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
      <Modal
        title={`Errors - ${errorRecord?.fileName ?? ""}`}
        open={!!errorRecord}
        footer={null}
        onCancel={() => setErrorRecord(null)}
      >
        <ul style={{ paddingLeft: 20 }}>
          {(errorRecord?.errors ?? []).map((error, index) => (
            <li key={index}>
              {error.source ? `[${error.source}] ` : ""}
              {error.message}
            </li>
          ))}
        </ul>
      </Modal>
    </List>
  );
};
